/**
 * Block Component Definition
 *
 * A general-purpose div container that defaults to display:block.
 * Unlike Row (flex by default) or Container (flex by default), Block
 * behaves like a standard HTML div. It can be switched to flex or grid
 * when needed. The go-to wrapper for grouping content, applying backgrounds,
 * or creating layout sections without opinionated flex defaults.
 */

import { getGapSize, getResponsiveDirection } from '../../renderer/StyleResolver';

export const blockComponent = {
  type: 'block',
  label: 'Block',
  icon: 'Square',
  description: 'General-purpose block container',
  category: 'layout',

  canHaveChildren: true,

  resolveStyles(props, breakpoint) {
    const displayMode = props.display || 'block';
    const isFlex = displayMode === 'flex' || displayMode === 'inline-flex';
    const gap = getGapSize(props.gap);

    const styles = {
      display: displayMode,
    };

    if (isFlex) {
      const direction = getResponsiveDirection(props.direction, breakpoint);
      styles.flexDirection = direction === 'vertical' ? 'column' : 'row';
      styles.flexWrap = props.flexWrap || 'wrap';
      styles.alignItems = props.alignment || 'stretch';
      styles.justifyContent = props.justifyContent || 'flex-start';
      styles.gap = gap;
    } else if (displayMode === 'grid') {
      styles.gap = gap;
    }

    // Background image (block-specific, with size/position/repeat options)
    if (props.backgroundImageUrl) {
      styles.backgroundImage = `url(${props.backgroundImageUrl})`;
      styles.backgroundSize = props.backgroundSize || 'cover';
      styles.backgroundPosition = props.backgroundPosition || 'center center';
      styles.backgroundRepeat = props.backgroundRepeat || 'no-repeat';
    }

    return styles;
  },

  defaultProps: {
    // Layout
    display: 'block',
    direction: { base: 'vertical', tablet: null, desktop: null },
    gap: 'none',
    alignment: 'stretch',
    justifyContent: 'flex-start',
    flexWrap: 'wrap',

    // Size
    width: { base: { value: 'auto', unit: 'px' }, tablet: null, desktop: null },
    height: { base: { value: 'auto', unit: 'px' }, tablet: null, desktop: null },
    minWidth: { base: { value: 'auto', unit: 'px' }, tablet: null, desktop: null },
    minHeight: { base: { value: 50, unit: 'px' }, tablet: null, desktop: null },
    maxWidth: { base: { value: 'auto', unit: 'px' }, tablet: null, desktop: null },
    maxHeight: { base: { value: 'auto', unit: 'px' }, tablet: null, desktop: null },

    // Spacing
    padding: { base: { top: 0, bottom: 0, left: 0, right: 0, unit: 'px' }, tablet: null, desktop: null },
    margin: { base: { top: 0, bottom: 0, left: 0, right: 0, unit: 'px' }, tablet: null, desktop: null },

    // Appearance
    backgroundColor: 'transparent',
    backgroundImageUrl: '',
    backgroundSize: 'cover',
    backgroundPosition: 'center center',
    backgroundRepeat: 'no-repeat',
    opacity: 100,

    // Border
    borderWidth: 0,
    borderStyle: 'solid',
    borderColor: 'transparent',
    borderRadius: 0,

    // Advanced
    overflow: 'visible',
    visibility: { base: true, tablet: null, desktop: null },

    // Positioning
    position: 'static',
    top: { base: { value: 'auto', unit: 'px' }, tablet: null, desktop: null },
    right: { base: { value: 'auto', unit: 'px' }, tablet: null, desktop: null },
    bottom: { base: { value: 'auto', unit: 'px' }, tablet: null, desktop: null },
    left: { base: { value: 'auto', unit: 'px' }, tablet: null, desktop: null },
    zIndex: 'auto',

    // Custom CSS
    customCSS: '',

    // Flex child (when inside a flex parent) — responsive
    flexChild: { base: { mode: 'auto', basis: 50 }, tablet: null, desktop: null },
  },

  settingsSchema: [
    // Layout category
    {
      key: 'display',
      type: 'iconToggle',
      label: 'Display',
      category: 'Layout',
      default: 'block',
      options: [
        { value: 'block', label: 'Block', icon: 'Square' },
        { value: 'flex', label: 'Flex', icon: 'Rows3' },
        { value: 'grid', label: 'Grid', icon: 'LayoutGrid' },
        { value: 'inline-block', label: 'Inline', icon: 'AlignHorizontalJustifyStart' },
        { value: 'inline-flex', label: 'Inline Flex', icon: 'Columns' },
        { value: 'none', label: 'None', icon: 'EyeOff' },
      ],
    },
    {
      key: 'direction',
      type: 'responsiveDirection',
      label: 'Direction',
      category: 'Layout',
      description: 'Horizontal (side by side) or vertical (stacked)',
      showWhen: { field: 'display', in: ['flex', 'inline-flex'] },
    },
    {
      key: 'gap',
      type: 'select',
      label: 'Gap',
      category: 'Layout',
      default: 'none',
      options: [
        { value: 'none', label: 'None' },
        { value: 'sm', label: 'Small (8px)' },
        { value: 'md', label: 'Medium (16px)' },
        { value: 'lg', label: 'Large (24px)' },
        { value: 'xl', label: 'Extra Large (32px)' },
      ],
      showWhen: { field: 'display', in: ['flex', 'inline-flex', 'grid'] },
    },
    {
      key: 'alignment',
      type: 'iconToggle',
      label: 'Align',
      category: 'Layout',
      default: 'stretch',
      options: [
        { value: 'flex-start', label: 'Start', icon: 'AlignStartVertical' },
        { value: 'center', label: 'Center', icon: 'AlignCenterVertical' },
        { value: 'flex-end', label: 'End', icon: 'AlignEndVertical' },
        { value: 'stretch', label: 'Stretch', icon: 'AlignVerticalSpaceAround' },
        { value: 'baseline', label: 'Baseline', icon: 'Baseline' },
      ],
      showWhen: { field: 'display', in: ['flex', 'inline-flex'] },
    },
    {
      key: 'justifyContent',
      type: 'iconToggle',
      label: 'Justify',
      category: 'Layout',
      default: 'flex-start',
      options: [
        { value: 'flex-start', label: 'Start', icon: 'AlignHorizontalJustifyStart' },
        { value: 'center', label: 'Center', icon: 'AlignHorizontalJustifyCenter' },
        { value: 'flex-end', label: 'End', icon: 'AlignHorizontalJustifyEnd' },
        { value: 'space-between', label: 'Between', icon: 'AlignHorizontalSpaceBetween' },
        { value: 'space-around', label: 'Around', icon: 'AlignHorizontalSpaceAround' },
        { value: 'space-evenly', label: 'Evenly', icon: 'AlignHorizontalDistributeCenter' },
      ],
      showWhen: { field: 'display', in: ['flex', 'inline-flex'] },
    },
    {
      key: 'flexWrap',
      type: 'iconToggle',
      label: 'Wrap',
      category: 'Layout',
      default: 'wrap',
      options: [
        { value: 'nowrap', label: 'No Wrap', icon: 'WrapText' },
        { value: 'wrap', label: 'Wrap', icon: 'TextCursorInput' },
      ],
      showWhen: { field: 'display', in: ['flex', 'inline-flex'] },
    },

    // Size category
    {
      key: 'width',
      type: 'responsiveDimension',
      label: 'Width',
      category: 'Size',
    },
    {
      key: 'height',
      type: 'responsiveDimension',
      label: 'Height',
      category: 'Size',
    },
    {
      key: 'minWidth',
      type: 'responsiveDimension',
      label: 'Min Width',
      category: 'Size',
    },
    {
      key: 'minHeight',
      type: 'responsiveDimension',
      label: 'Min Height',
      category: 'Size',
    },
    {
      key: 'maxWidth',
      type: 'responsiveDimension',
      label: 'Max Width',
      category: 'Size',
    },
    {
      key: 'maxHeight',
      type: 'responsiveDimension',
      label: 'Max Height',
      category: 'Size',
    },

    // Spacing category
    {
      key: 'padding',
      type: 'responsivePadding',
      label: 'Padding',
      category: 'Spacing',
    },
    {
      key: 'margin',
      type: 'responsiveMargin',
      label: 'Margin',
      category: 'Spacing',
    },

    // Appearance category
    {
      key: 'backgroundColor',
      type: 'color',
      label: 'Background Color',
      category: 'Appearance',
      default: 'transparent',
      allowGradient: true,
    },
    {
      key: 'backgroundImageUrl',
      type: 'mediaPicker',
      label: 'Background Image',
      category: 'Appearance',
      acceptTypes: 'image',
      placeholder: 'https://...',
    },
    {
      key: 'backgroundSize',
      type: 'select',
      label: 'Background Size',
      category: 'Appearance',
      default: 'cover',
      options: [
        { value: 'cover', label: 'Cover' },
        { value: 'contain', label: 'Contain' },
        { value: 'auto', label: 'Auto' },
        { value: '100% 100%', label: 'Stretch' },
      ],
      showWhen: { field: 'backgroundImageUrl', notEquals: '' },
    },
    {
      key: 'backgroundPosition',
      type: 'select',
      label: 'Background Position',
      category: 'Appearance',
      default: 'center center',
      options: [
        { value: 'left top', label: 'Top Left' },
        { value: 'center top', label: 'Top Center' },
        { value: 'right top', label: 'Top Right' },
        { value: 'left center', label: 'Center Left' },
        { value: 'center center', label: 'Center' },
        { value: 'right center', label: 'Center Right' },
        { value: 'left bottom', label: 'Bottom Left' },
        { value: 'center bottom', label: 'Bottom Center' },
        { value: 'right bottom', label: 'Bottom Right' },
      ],
      showWhen: { field: 'backgroundImageUrl', notEquals: '' },
    },
    {
      key: 'backgroundRepeat',
      type: 'select',
      label: 'Background Repeat',
      category: 'Appearance',
      default: 'no-repeat',
      options: [
        { value: 'no-repeat', label: 'No Repeat' },
        { value: 'repeat', label: 'Repeat' },
        { value: 'repeat-x', label: 'Repeat X' },
        { value: 'repeat-y', label: 'Repeat Y' },
      ],
      showWhen: { field: 'backgroundImageUrl', notEquals: '' },
    },
    {
      key: 'opacity',
      type: 'number',
      label: 'Opacity (%)',
      category: 'Appearance',
      min: 0,
      max: 100,
      default: 100,
    },

    // Border category
    {
      key: 'borderWidth',
      type: 'number',
      label: 'Border Width (px)',
      category: 'Border',
      min: 0,
      max: 20,
      default: 0,
    },
    {
      key: 'borderStyle',
      type: 'select',
      label: 'Border Style',
      category: 'Border',
      default: 'solid',
      options: [
        { value: 'none', label: 'None' },
        { value: 'solid', label: 'Solid' },
        { value: 'dashed', label: 'Dashed' },
        { value: 'dotted', label: 'Dotted' },
      ],
    },
    {
      key: 'borderColor',
      type: 'color',
      label: 'Border Color',
      category: 'Border',
      default: 'transparent',
    },
    {
      key: 'borderRadius',
      type: 'number',
      label: 'Border Radius (px)',
      category: 'Border',
      min: 0,
      max: 100,
      default: 0,
    },

    // Advanced category
    {
      key: 'overflow',
      type: 'select',
      label: 'Overflow',
      category: 'Advanced',
      default: 'visible',
      options: [
        { value: 'visible', label: 'Visible' },
        { value: 'hidden', label: 'Hidden' },
        { value: 'scroll', label: 'Scroll' },
        { value: 'auto', label: 'Auto' },
        { value: 'clip', label: 'Clip' },
      ],
    },
    {
      key: 'visibility',
      type: 'responsiveVisibility',
      label: 'Visibility',
      category: 'Advanced',
      default: { base: true, tablet: null, desktop: null },
    },

    // Position category
    {
      key: 'position',
      type: 'select',
      label: 'Position',
      category: 'Position',
      default: 'static',
      options: [
        { value: 'static', label: 'Static (Normal)' },
        { value: 'relative', label: 'Relative' },
        { value: 'absolute', label: 'Absolute' },
        { value: 'fixed', label: 'Fixed' },
        { value: 'sticky', label: 'Sticky' },
      ],
    },
    {
      key: 'top',
      type: 'responsiveDimension',
      label: 'Top',
      category: 'Position',
      showWhen: { field: 'position', notEquals: 'static' },
    },
    {
      key: 'right',
      type: 'responsiveDimension',
      label: 'Right',
      category: 'Position',
      showWhen: { field: 'position', notEquals: 'static' },
    },
    {
      key: 'bottom',
      type: 'responsiveDimension',
      label: 'Bottom',
      category: 'Position',
      showWhen: { field: 'position', notEquals: 'static' },
    },
    {
      key: 'left',
      type: 'responsiveDimension',
      label: 'Left',
      category: 'Position',
      showWhen: { field: 'position', notEquals: 'static' },
    },
    {
      key: 'zIndex',
      type: 'number',
      label: 'Z-Index',
      category: 'Position',
      placeholder: 'auto',
      showWhen: { field: 'position', notEquals: 'static' },
    },

    // Custom CSS
    {
      key: 'customCSS',
      type: 'customCSS',
      label: 'Custom CSS',
      category: 'Advanced',
      default: '',
    },

    // Flex Child sizing (when this block is inside a flex parent)
    {
      key: 'flexChild',
      type: 'flexChild',
      label: 'Flex Child',
      category: 'Layout',
      showWhenParent: { field: 'display', in: ['flex', 'inline-flex'] },
      description: 'Controls sizing when this block is inside a flex container',
      default: { base: { mode: 'auto', basis: 50 }, tablet: null, desktop: null },
    },
  ],

  render: null,
};

export default blockComponent;
